import {
  getMedicalTreatmentRecordStatusClassName,
  getMedicalTreatmentRecordStatusLabel,
} from "@/components/features/matter/medical-treatments/AppointmentRecordStatusIcon";
import { MatterTreatmentRecordContextMenu } from "@/components/features/matter/context-menus/MatterTreatmentRecordContextMenu";
import { MedicalRecordPreviewFiles } from "@/components/features/matter/medical-treatments/MedicalRecordPreviewFiles";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { Typography } from "@/components/ui/Typography/Typography";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useDeleteMedicalTreatmentRecord } from "@/lib/data/react-query/matters/medical-treatment-record";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { type Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatDate, trimText } from "@/lib/utils/format";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import { Eye, FileText } from "lucide-react";
import { useMemo, useState } from "react";

interface MatterTreatmentMedicalRecordsCardProps {
  records: TreatmentViewModel["records"];
  matter: Matter;
}

export function MatterTreatmentMedicalRecordsCard({
  records,
  matter,
}: MatterTreatmentMedicalRecordsCardProps) {
  const { hasActionPermission } = useUser();
  const { openTreatmentRecordModal } = useGlobalModal();
  const deleteRecord = useDeleteMedicalTreatmentRecord();
  const [previewRecordId, setPreviewRecordId] = useState<string | null>(null);

  const canEditRecords = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteRecords = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatmentRecord,
    ACTION_PERMISSION_VALUES.delete,
  );
  const selectedPreviewRecord = useMemo(
    () => records.find((record) => record.id === previewRecordId) ?? null,
    [records, previewRecordId],
  );
  const previewRecordFiles = selectedPreviewRecord
    ? mergeFilesAndReferenceId(
        matter,
        "medicalTreatmentRecordId",
        selectedPreviewRecord.id,
      )
    : [];

  const openEditRecord = (record: TreatmentViewModel["records"][number]) => {
    if (!canEditRecords) return;

    openTreatmentRecordModal({
      matterId: matter.id,
      recordId: record.id,
      treatmentId: record.medicalTreatmentId,
      editing: true,
      initialTab: "details",
    });
  };

  return (
    <>
      <div>
        {records.length === 0 ? (
          <div className="bg-card m-4 rounded-lg border border-dashed p-4 text-center">
            <Typography variant="subHeading" size="sm">
              No records
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/50 text-muted-foreground border-b text-xs font-medium">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">
                    Record name
                  </th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Requested</th>
                  <th className="px-4 py-2 text-left font-medium">
                    Received / rejected
                  </th>
                  <th className="w-12 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {records.map((record) => {
                  const status = record.status;
                  const statusDate =
                    status === "Rejected"
                      ? (record.rejectedDate ?? undefined)
                      : (record.receivedDate ?? undefined);

                  const label = trimText(record.name) || "-";

                  return (
                    <MatterTreatmentRecordContextMenu
                      key={record.id}
                      canDelete={canDeleteRecords}
                      id={record.id}
                      isDeleting={deleteRecord.isPending}
                      label={label}
                      matter={matter}
                      onDelete={async () => {
                        await deleteRecord.mutateAsync({
                          matterId: matter.id,
                          id: record.id,
                        });
                      }}
                      onEdit={() => {
                        openEditRecord(record);
                      }}
                      status={status}
                      trigger={
                        <tr
                          className={cn(
                            "border-b last:border-b-0",
                            canEditRecords &&
                              "hover:bg-muted/50 cursor-pointer",
                          )}
                          onClick={() => {
                            openEditRecord(record);
                          }}
                        >
                          <td className="text-foreground px-4 py-3 font-medium">
                            {label}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 text-xs font-medium",
                                getMedicalTreatmentRecordStatusClassName(
                                  status,
                                  record.hasReference,
                                ),
                              )}
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-current" />
                              {getMedicalTreatmentRecordStatusLabel(
                                status,
                                record.hasReference,
                              )}
                            </span>
                          </td>
                          <td className="text-muted-foreground px-4 py-3">
                            {record.requestDate
                              ? formatDate(record.requestDate)
                              : "-"}
                          </td>
                          <td className="text-muted-foreground px-4 py-3">
                            {statusDate ? formatDate(statusDate) : "-"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPreviewRecordId(record.id);
                              }}
                              data-testid="preview-medical-treatment-record-button"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      }
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Dialog
        open={Boolean(selectedPreviewRecord)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPreviewRecordId(null);
          }
        }}
      >
        <DialogContent className="flex h-[96vh] max-h-[96vh] w-[96vw] max-w-6xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-border border-b px-5 py-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-muted-foreground h-4 w-4" />
              Medical record preview
            </DialogTitle>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden p-4">
            <MedicalRecordPreviewFiles
              files={previewRecordFiles}
              emptyMessage="No file is referenced on this medical record."
              viewerHeightClassName="h-full"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
